import {
  defaultInputs,
  defaultStyles,
  getIndicatorDefinition
} from "./indicator-definitions.js";

export const MAX_INDICATORS = 24;
export const MAX_INDICATOR_PANES = 4;

let fallbackSequence = 0;

export function createIndicatorInstance(definitionOrId, {
  id = createStableId(),
  paneId = null,
  scale = "right",
  visible = true,
  inputs = null,
  styles = null
} = {}) {
  const definition = typeof definitionOrId === "string"
    ? getIndicatorDefinition(definitionOrId)
    : definitionOrId;
  if (!definition) throw new Error("The requested indicator definition is unavailable.");
  return {
    id,
    definitionId: definition.id,
    displayName: definition.abbreviation,
    paneId: paneId || (definition.defaultPane === "main" ? "main" : "pane-2"),
    scale,
    visible,
    order: 0,
    inputs: { ...defaultInputs(definition), ...(inputs || {}) },
    styles: mergeStyles(defaultStyles(definition), styles),
    outputs: {},
    error: null
  };
}

export function createDefaultIndicatorInstances() {
  return normalizeIndicatorOrder([
    createIndicatorInstance("sma", {
      paneId: "main",
      inputs: { length: 20, source: "close" }
    }),
    createIndicatorInstance("ema", {
      paneId: "main",
      inputs: { length: 50, source: "close" }
    })
  ]);
}

export function cloneIndicatorInstances(instances) {
  return instances.map((instance) => ({
    ...instance,
    inputs: structuredClone(instance.inputs || {}),
    styles: structuredClone(instance.styles || {}),
    outputs: instance.outputs || {},
    error: instance.error || null
  }));
}

export function serializeIndicatorInstancesForWorkspace(instances) {
  return instances.map((instance) => ({
    id: instance.id,
    definitionId: instance.definitionId,
    displayName: instance.displayName,
    paneId: instance.paneId,
    scale: instance.scale,
    visible: instance.visible,
    order: instance.order,
    inputs: structuredClone(instance.inputs || {}),
    styles: structuredClone(instance.styles || {})
  }));
}

export function resetIndicatorInstance(instance) {
  const definition = getIndicatorDefinition(instance.definitionId);
  if (!definition) return { ...instance };
  return {
    ...instance,
    inputs: defaultInputs(definition),
    styles: defaultStyles(definition),
    error: null
  };
}

export function normalizeIndicatorOrder(instances) {
  const paneOrder = [];
  for (const instance of instances) {
    if (!paneOrder.includes(instance.paneId)) paneOrder.push(instance.paneId);
  }
  if (!paneOrder.includes("main")) paneOrder.unshift("main");
  return instances.map((instance) => ({
    ...instance,
    order: instances
      .filter((candidate) => candidate.paneId === instance.paneId)
      .findIndex((candidate) => candidate.id === instance.id)
  })).sort((left, right) => {
    const paneDifference = paneOrder.indexOf(left.paneId) - paneOrder.indexOf(right.paneId);
    return paneDifference || left.order - right.order;
  });
}

export function nextPaneId(instances) {
  const used = new Set(instances.map((instance) => instance.paneId));
  for (let index = 2; index <= MAX_INDICATOR_PANES; index += 1) {
    const candidate = `pane-${index}`;
    if (!used.has(candidate)) return candidate;
  }
  return null;
}

export function paneIds(instances) {
  const ids = ["main"];
  for (const instance of instances) {
    if (!ids.includes(instance.paneId)) ids.push(instance.paneId);
  }
  return ids;
}

export function inputSummary(instance) {
  const definition = getIndicatorDefinition(instance.definitionId);
  if (!definition) return "Definition unavailable";
  const parts = [];
  for (const key of definition.summaryInputs || []) {
    const input = definition.inputs.find((candidate) => candidate.key === key);
    const value = instance.inputs[key];
    if (value === undefined || value === null || value === "") continue;
    const option = input?.options?.find((candidate) => candidate.value === value);
    if (key === "source") parts.push(option?.label || capitalize(value));
    else if (key.toLowerCase().includes("length")) parts.push(`${input?.label || key} ${value}`);
    else parts.push(`${input?.label || key} ${option?.label || value}`);
  }
  return parts.join(" · ") || definition.description;
}

export function paneLabel(paneId, symbol = "") {
  if (paneId === "main") return `Main chart — ${symbol || "Symbol"}`;
  const number = Number.parseInt(paneId.split("-")[1], 10);
  return `Pane ${Number.isFinite(number) ? number : paneId}`;
}

export function validateInput(definition, key, value, currentInputs = {}) {
  const input = definition?.inputs.find((candidate) => candidate.key === key);
  if (!input) return { valid: false, message: "This input is no longer available." };
  if (input.type === "boolean") return { valid: true, value: value === true };
  if (["select", "source"].includes(input.type)) {
    const supported = input.options?.some((option) => option.value === value);
    return supported
      ? { valid: true, value }
      : { valid: false, message: `Choose a valid ${input.label.toLowerCase()}.` };
  }
  if (input.type === "color") {
    return /^#[0-9a-f]{6}$/i.test(String(value))
      ? { valid: true, value: String(value).toLowerCase() }
      : { valid: false, message: "Enter a valid six-digit color." };
  }

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return { valid: false, message: `${input.label} must be a number.` };
  }
  if (numeric < input.minimum || numeric > input.maximum) {
    return {
      valid: false,
      message: `${input.label} must be between ${input.minimum} and ${input.maximum}.`
    };
  }
  if (input.type === "integer" && !Number.isInteger(numeric)) {
    return { valid: false, message: `${input.label} must be a whole number.` };
  }
  if (definition.id === "macd") {
    const fast = key === "fastLength" ? numeric : Number(currentInputs.fastLength);
    const slow = key === "slowLength" ? numeric : Number(currentInputs.slowLength);
    if (Number.isFinite(fast) && Number.isFinite(slow) && fast >= slow) {
      return { valid: false, message: "Fast length must be less than slow length." };
    }
  }
  return { valid: true, value: numeric };
}

function mergeStyles(defaults, styles) {
  if (!styles) return defaults;
  return Object.fromEntries(Object.entries(defaults).map(([key, value]) => [
    key,
    { ...value, ...(styles[key] || {}) }
  ]));
}

function createStableId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  fallbackSequence += 1;
  return `indicator-${Date.now().toString(36)}-${fallbackSequence.toString(36)}`;
}

function capitalize(value) {
  const text = String(value);
  return text ? text[0].toUpperCase() + text.slice(1) : text;
}
